import React, { useState } from 'react';
import { Box, Typography, Button, Avatar, Dialog, IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CloseIcon from '@mui/icons-material/Close';
import WifiTetheringIcon from '@mui/icons-material/WifiTethering';
import PublicIcon from '@mui/icons-material/Public';
import StarIcon from '@mui/icons-material/Star';
import MovieCreationRoundedIcon from '@mui/icons-material/MovieCreationRounded';
import StarsRoundedIcon from '@mui/icons-material/StarsRounded';
import ColorLensRoundedIcon from '@mui/icons-material/ColorLensRounded';
import GraphicEqRoundedIcon from '@mui/icons-material/GraphicEqRounded';
import { useTheme } from '../../../ThemeContext';
import heroDark from '../../../assets/hero_dark.png';
import heroLight from '../../../assets/hero_light.png';

const BARS = [7, 13, 8, 18, 11, 16, 9, 22, 13, 19];

const Introduction = () => {
  const navigate = useNavigate();
  const [videoOpen, setVideoOpen] = useState(false);
  const { isDarkMode } = useTheme();
  const heroImage = isDarkMode ? heroDark : heroLight;

  /* ── theme tokens ── */
  const textPrimary = isDarkMode ? '#ffffff' : '#0d1530';
  const textMuted   = isDarkMode ? '#7a8baa' : '#6b7a99';
  const cardBg      = isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.88)';
  const cardBorder  = isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(7, 164, 245,0.14)';
  const cardShadow  = isDarkMode ? '0 8px 32px rgba(0,0,0,0.35)' : '0 8px 32px rgba(7, 164, 245,0.12)';

  return (
    <Box
      component="section"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        background: isDarkMode
          ? 'linear-gradient(135deg,#050c1f 0%,#081230 45%,#06101f 100%)'
          : 'linear-gradient(135deg,#f0f4ff 0%,#ffffff 50%,#eef2ff 100%)',
        minHeight: { xs: 'auto', md: '80vh' },
        mt: { xs: '-85px', md: '-100px' }, // Pull up to remove the gap and slide under the header
        pt: { xs: '120px', md: '140px' },  // Push content down to avoid overlapping the header
        pb: { xs: 4, md: 0 },
        display: 'flex',
        flexDirection: 'column',
        transition: 'background 0.4s ease',
      }}
    >
      {/* Ambient glows */}
      <Box sx={{ position:'absolute', top:'-15%', left:'-10%', width:600, height:600, background:'radial-gradient(circle,rgba(7, 164, 245,0.2) 0%,transparent 70%)', filter:'blur(80px)', pointerEvents:'none', zIndex:0 }} />
      <Box sx={{ position:'absolute', top:'10%', right:'-8%', width:500, height:500, background:'radial-gradient(circle,rgba(20,60,180,0.25) 0%,transparent 68%)', filter:'blur(90px)', pointerEvents:'none', zIndex:0 }} />

      {/* ══════════════════════════════════════
          LEFT + RIGHT — 2-column grid (z:1)
      ══════════════════════════════════════ */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          flexGrow: 1,
          px: { xs: 2, sm: 3, md: 5 },
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* ── LEFT ── */}
        <Box sx={{ display:'flex', alignItems:'flex-start', zIndex: 2, pt: { xs: 2, md: 4 } }}>
          <motion.div initial={{ opacity:0, x:-50 }} animate={{ opacity:1, x:0 }} transition={{ duration:0.8 }}>
            <Box sx={{ pr:{ md: 2 }, maxWidth: 380 }}>

              {/* WHO WE ARE badge */}
              <Box sx={{ display:'inline-flex', alignItems:'center', background: isDarkMode?'rgba(255,255,255,0.09)':'rgba(7, 164, 245,0.08)', border: isDarkMode?'1px solid rgba(255,255,255,0.22)':'1px solid rgba(7, 164, 245,0.25)', color: isDarkMode?'rgba(255,255,255,0.88)':'#07a4f5', px:2, py:0.6, borderRadius:'6px', fontSize:'0.7rem', fontWeight:700, mb:2.5, letterSpacing:'0.1em', textTransform:'uppercase' }}>
                WHO WE ARE
              </Box>

              <Typography variant="h2" fontWeight={900} sx={{ color:textPrimary, lineHeight:1.12, fontSize:{ xs:'2.4rem', md:'2.7rem' }, mb:2 }}>
                Connecting<br />Creators.{' '}
                <Box component="span" sx={{ background:'linear-gradient(90deg,#4f83f7,#5dade2)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', fontStyle:'italic' }}>
                  Inspiring
                </Box>{' '}
                the World.
              </Typography>

              <Typography sx={{ color:textMuted, fontSize:'0.9rem', lineHeight:1.75, mb:3.5 }}>
                Filmhook Mediaapps Pvt Ltd is a forward-thinking company in the entertainment sector, delivering powerful digital solutions for the film and media industry.
              </Typography>

              <Box sx={{ display:'flex', gap:2, flexWrap:'wrap' }}>
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/products')}
                  sx={{ background:'linear-gradient(135deg,#07a4f5 0%,#5dade2 100%)', borderRadius:'50px', px:3.5, py:1.3, fontWeight:700, fontSize:'0.88rem', textTransform:'none', boxShadow:'0 8px 24px rgba(7, 164, 245,0.4)', '&:hover':{ transform:'translateY(-2px)', boxShadow:'0 12px 32px rgba(7, 164, 245,0.55)' }, transition:'all 0.25s' }}
                >
                  Discover More
                </Button>
                <Box sx={{ display:'flex', alignItems:'center', gap:1, cursor:'pointer', '&:hover .play-icon':{ transform:'scale(1.15)' } }} onClick={() => setVideoOpen(true)}>
                  <Box className="play-icon" sx={{ width:42, height:42, borderRadius:'50%', background: isDarkMode?'rgba(255,255,255,0.1)':'rgba(7, 164, 245,0.1)', border: isDarkMode?'1px solid rgba(255,255,255,0.25)':'1px solid rgba(7, 164, 245,0.25)', display:'flex', alignItems:'center', justifyContent:'center', transition:'transform 0.25s' }}>
                    <PlayArrowIcon sx={{ fontSize:'1.15rem', color: isDarkMode?'white':'#07a4f5' }} />
                  </Box>
                  <Typography sx={{ color:textMuted, fontSize:'0.85rem', fontWeight:600 }}>Watch Showreel</Typography>
                </Box>
              </Box>

            </Box>
          </motion.div>
        </Box>

        {/* ── RIGHT (Floating Cards) ── */}
        <Box sx={{ display:'flex', alignItems:'flex-start', justifyContent:'flex-end', zIndex: 2, pb: { xs: 6, md: 0 }, mt: { xs: 4, md: 0 }, pt: { xs: 2, md: 4 } }}>
          <motion.div initial={{ opacity:0, x:50 }} animate={{ opacity:1, x:0 }} transition={{ duration:0.8, delay:0.25 }}>
            <Box sx={{ display:'flex', flexDirection:'column', gap:2.5, maxWidth: 280 }}>

              {/* Live Streaming */}
              <motion.div animate={{ y:[0,-8,0] }} transition={{ repeat:Infinity, duration:4, ease:'easeInOut' }}>
                <Box sx={{ background:cardBg, backdropFilter:'blur(20px)', border:cardBorder, borderRadius:'16px', p:2, boxShadow:cardShadow }}>
                  <Box sx={{ display:'flex', alignItems:'center', gap:1.5, mb:1.4 }}>
                    <Box sx={{ width:40, height:40, borderRadius:'50%', background:'linear-gradient(135deg,#07a4f5,#5dade2)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      <WifiTetheringIcon sx={{ fontSize:'1.2rem', color:'white' }} />
                    </Box>
                    <Box>
                      <Typography sx={{ color:textPrimary, fontSize:'0.85rem', fontWeight:700, lineHeight:1.1 }}>Live Streaming</Typography>
                      <Typography sx={{ color:textMuted, fontSize:'0.7rem' }}>12.5K Watching</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display:'flex', alignItems:'flex-end', gap:'3px', height:26 }}>
                    {BARS.map((h, i) => <Box key={i} sx={{ width:6, height:`${h}px`, background:i>6?'#5dade2':'rgba(93,173,226,0.28)', borderRadius:'2px 2px 0 0' }} />)}
                  </Box>
                </Box>
              </motion.div>

              {/* Global Community */}
              <motion.div animate={{ y:[0,10,0] }} transition={{ repeat:Infinity, duration:4.8, ease:'easeInOut' }}>
                <Box sx={{ background:cardBg, backdropFilter:'blur(20px)', border:cardBorder, borderRadius:'16px', p:2, boxShadow:cardShadow }}>
                  <Box sx={{ display:'flex', alignItems:'center', gap:1.4, mb:1 }}>
                    <Box sx={{ width:40, height:40, borderRadius:'50%', background:'rgba(107,70,193,0.25)', border:'1px solid rgba(107,70,193,0.45)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      <PublicIcon sx={{ fontSize:'1.2rem', color:'#9b7fe8' }} />
                    </Box>
                    <Typography sx={{ color:textPrimary, fontSize:'0.85rem', fontWeight:700 }}>Global Community</Typography>
                  </Box>
                  <Typography sx={{ color:textMuted, fontSize:'0.72rem', lineHeight:1.6, mb:1.4 }}>Connect with creators and audiences worldwide.</Typography>
                  <Box sx={{ display:'flex', alignItems:'center', gap:0.3 }}>
                    {[['#07a4f5','R'],['#e91e63','J'],['#ff9800','K'],['#4caf50','M'],['#9c27b0','S']].map(([bg,l],i) => (
                      <Avatar key={i} sx={{ width:26, height:26, fontSize:'0.58rem', fontWeight:800, background:bg, border:`2px solid ${isDarkMode?'rgba(5,12,31,0.9)':'#fff'}`, ml:i===0?0:-0.7 }}>{l}</Avatar>
                    ))}
                    <Typography sx={{ color:'#5dade2', ml:1.2, fontSize:'0.75rem', fontWeight:800 }}>+12K</Typography>
                  </Box>
                </Box>
              </motion.div>

              {/* Share Your Story */}
              <motion.div animate={{ y:[0,-7,0] }} transition={{ repeat:Infinity, duration:3.8, ease:'easeInOut' }}>
                <Box sx={{ background:cardBg, backdropFilter:'blur(20px)', border:cardBorder, borderRadius:'16px', p:2, boxShadow:cardShadow }}>
                  <Box sx={{ display:'flex', alignItems:'center', gap:1.4, mb:1 }}>
                    <Box sx={{ width:40, height:40, borderRadius:'50%', background:'rgba(233,30,99,0.2)', border:'1px solid rgba(233,30,99,0.4)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      <StarIcon sx={{ fontSize:'1.2rem', color:'#e91e63' }} />
                    </Box>
                    <Typography sx={{ color:textPrimary, fontSize:'0.85rem', fontWeight:700 }}>Share Your Story</Typography>
                  </Box>
                  <Typography sx={{ color:textMuted, fontSize:'0.72rem', lineHeight:1.6 }}>Express your creativity and inspire millions.</Typography>
                </Box>
              </motion.div>

            </Box>
          </motion.div>
        </Box>
      </Box>

      {/* ══════════════════════════════════════
          CENTER — Hero Image overlay (z:10)
      ══════════════════════════════════════ */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-end',
          zIndex: 10,
          pointerEvents: 'none', // pass clicks through to left/right
        }}
      >
        <motion.div
          animate={{ y: [0, -14, 0] }}
          transition={{ repeat: Infinity, duration: 5.5, ease: 'easeInOut' }}
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', pointerEvents: 'none' }}
        >
          <Box
            sx={{
              width: { xs: '90vw', md: '50vw' },
              maxWidth: 900,
              pointerEvents: 'none',
              flexShrink: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-end',
              mb: { xs: 0, md: '-20px' },
              position: 'relative',
            }}
          >
            {/* Ambient Volumetric Halo on Pedestal */}
            <Box
              sx={{
                position: 'absolute',
                bottom: '12%',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '80%',
                height: '130px',
                borderRadius: '50%',
                background: isDarkMode
                  ? 'radial-gradient(ellipse, rgba(7, 164, 245, 0.45) 0%, rgba(56, 189, 248, 0.15) 50%, transparent 75%)'
                  : 'radial-gradient(ellipse, rgba(7, 164, 245, 0.28) 0%, rgba(2, 132, 199, 0.1) 50%, transparent 75%)',
                filter: 'blur(28px)',
                pointerEvents: 'none',
              }}
            />

            {/* 1. Prop 1: 🎬 Clapperboard & 4K DCI Slate Badge (Top-Left) */}
            <Box
              component={motion.div}
              animate={{ y: [0, -18, 0], rotate: [-4, 2, -4] }}
              transition={{ repeat: Infinity, duration: 4.8, ease: 'easeInOut' }}
              sx={{
                position: 'absolute',
                top: { xs: '15%', md: '18%' },
                left: { xs: '-4%', md: '-8%', lg: '-12%' },
                display: { xs: 'none', sm: 'flex' },
                alignItems: 'center',
                gap: 1.2,
                px: 2,
                py: 1,
                borderRadius: '16px',
                background: isDarkMode ? 'rgba(7, 16, 34, 0.85)' : 'rgba(255, 255, 255, 0.92)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                border: isDarkMode ? '1.5px solid rgba(7, 164, 245, 0.4)' : '1.5px solid rgba(2, 132, 199, 0.3)',
                boxShadow: isDarkMode ? '0 14px 35px rgba(0,0,0,0.6), 0 0 25px rgba(7, 164, 245, 0.3)' : '0 14px 35px rgba(15,23,42,0.12), 0 0 20px rgba(2, 132, 199, 0.2)',
                zIndex: 12,
                pointerEvents: 'auto',
                cursor: 'pointer',
                transition: 'transform 0.3s ease',
                '&:hover': { transform: 'scale(1.08) !important' },
              }}
            >
              <Box sx={{ width: 34, height: 34, borderRadius: '10px', background: 'linear-gradient(135deg, #07a4f5 0%, #0284c7 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                <MovieCreationRoundedIcon sx={{ fontSize: '1.2rem' }} />
              </Box>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#ef4444', boxShadow: '0 0 8px #ef4444', animation: 'propRec 1s infinite alternate', '@keyframes propRec': { '0%': { opacity: 1 }, '100%': { opacity: 0.3 } } }} />
                  <Typography sx={{ fontFamily: 'monospace', fontSize: '0.66rem', fontWeight: 800, color: '#ef4444', letterSpacing: '0.1em' }}>
                    REC • 4K DCI
                  </Typography>
                </Box>
                <Typography sx={{ color: isDarkMode ? '#ffffff' : '#0f172a', fontSize: '0.76rem', fontWeight: 800 }}>
                  SCENE 24A • TAKE 01
                </Typography>
              </Box>
            </Box>

            {/* 2. Prop 2: 🎞️ Golden 3D LUT Colorist Badge (Top-Right) */}
            <Box
              component={motion.div}
              animate={{ y: [0, 20, 0], rotate: [3, -3, 3] }}
              transition={{ repeat: Infinity, duration: 5.4, ease: 'easeInOut' }}
              sx={{
                position: 'absolute',
                top: { xs: '12%', md: '14%' },
                right: { xs: '-4%', md: '-8%', lg: '-12%' },
                display: { xs: 'none', sm: 'flex' },
                alignItems: 'center',
                gap: 1.2,
                px: 2,
                py: 1,
                borderRadius: '16px',
                background: isDarkMode ? 'rgba(7, 16, 34, 0.85)' : 'rgba(255, 255, 255, 0.92)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                border: isDarkMode ? '1.5px solid rgba(245, 158, 11, 0.45)' : '1.5px solid rgba(217, 119, 6, 0.35)',
                boxShadow: isDarkMode ? '0 14px 35px rgba(0,0,0,0.6), 0 0 25px rgba(245, 158, 11, 0.25)' : '0 14px 35px rgba(15,23,42,0.12), 0 0 20px rgba(245, 158, 11, 0.2)',
                zIndex: 12,
                pointerEvents: 'auto',
                cursor: 'pointer',
                transition: 'transform 0.3s ease',
                '&:hover': { transform: 'scale(1.08) !important' },
              }}
            >
              <Box sx={{ width: 34, height: 34, borderRadius: '10px', background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                <ColorLensRoundedIcon sx={{ fontSize: '1.2rem' }} />
              </Box>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                  {['#ef4444', '#10b981', '#07a4f5'].map((c, i) => (
                    <Box key={i} sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: c }} />
                  ))}
                  <Typography sx={{ fontFamily: 'monospace', fontSize: '0.64rem', fontWeight: 800, color: '#f59e0b', ml: 0.3, letterSpacing: '0.08em' }}>
                    3D LUT
                  </Typography>
                </Box>
                <Typography sx={{ color: isDarkMode ? '#ffffff' : '#0f172a', fontSize: '0.76rem', fontWeight: 800 }}>
                  KODAK 500T PROFILE
                </Typography>
              </Box>
            </Box>

            {/* 3. Prop 3: 🎭 Hollywood Verified Audition Tag (Mid-Left) */}
            <Box
              component={motion.div}
              animate={{ y: [0, -14, 0], rotate: [-2, 3, -2] }}
              transition={{ repeat: Infinity, duration: 4.2, ease: 'easeInOut' }}
              sx={{
                position: 'absolute',
                bottom: { xs: '26%', md: '30%' },
                left: { xs: '-6%', md: '-12%', lg: '-16%' },
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                gap: 1.2,
                px: 1.8,
                py: 0.9,
                borderRadius: '16px',
                background: isDarkMode ? 'rgba(7, 16, 34, 0.85)' : 'rgba(255, 255, 255, 0.92)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                border: isDarkMode ? '1.5px solid rgba(168, 85, 247, 0.45)' : '1.5px solid rgba(147, 51, 234, 0.35)',
                boxShadow: isDarkMode ? '0 14px 35px rgba(0,0,0,0.6), 0 0 25px rgba(168, 85, 247, 0.25)' : '0 14px 35px rgba(15,23,42,0.12), 0 0 20px rgba(147, 51, 234, 0.2)',
                zIndex: 12,
                pointerEvents: 'auto',
                cursor: 'pointer',
                transition: 'transform 0.3s ease',
                '&:hover': { transform: 'scale(1.08) !important' },
              }}
            >
              <Box sx={{ width: 32, height: 32, borderRadius: '10px', background: 'linear-gradient(135deg, #a855f7 0%, #7e22ce 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                <StarsRoundedIcon sx={{ fontSize: '1.2rem' }} />
              </Box>
              <Box>
                <Typography sx={{ fontFamily: 'monospace', fontSize: '0.64rem', fontWeight: 800, color: '#c084fc', letterSpacing: '0.08em' }}>
                  CASTING VERIFIED
                </Typography>
                <Typography sx={{ color: isDarkMode ? '#ffffff' : '#0f172a', fontSize: '0.74rem', fontWeight: 800 }}>
                  98.4% TALENT MATCH
                </Typography>
              </Box>
            </Box>

            {/* 4. Prop 4: 🎚️ 120 FPS NLE Timeline Waveform (Mid-Right) */}
            <Box
              component={motion.div}
              animate={{ y: [0, 16, 0], rotate: [2, -2, 2] }}
              transition={{ repeat: Infinity, duration: 5.0, ease: 'easeInOut' }}
              sx={{
                position: 'absolute',
                bottom: { xs: '24%', md: '28%' },
                right: { xs: '-6%', md: '-12%', lg: '-15%' },
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                gap: 1.2,
                px: 1.8,
                py: 0.9,
                borderRadius: '16px',
                background: isDarkMode ? 'rgba(7, 16, 34, 0.85)' : 'rgba(255, 255, 255, 0.92)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                border: isDarkMode ? '1.5px solid rgba(16, 185, 129, 0.45)' : '1.5px solid rgba(5, 150, 105, 0.35)',
                boxShadow: isDarkMode ? '0 14px 35px rgba(0,0,0,0.6), 0 0 25px rgba(16, 185, 129, 0.25)' : '0 14px 35px rgba(15,23,42,0.12), 0 0 20px rgba(5, 150, 105, 0.2)',
                zIndex: 12,
                pointerEvents: 'auto',
                cursor: 'pointer',
                transition: 'transform 0.3s ease',
                '&:hover': { transform: 'scale(1.08) !important' },
              }}
            >
              <Box sx={{ width: 32, height: 32, borderRadius: '10px', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                <GraphicEqRoundedIcon sx={{ fontSize: '1.2rem' }} />
              </Box>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '10px', mb: 0.3 }}>
                  {[40, 85, 60, 100, 75].map((h, i) => (
                    <motion.div
                      key={i}
                      animate={{ height: [`${h * 0.3}%`, `${h}%`, `${h * 0.4}%`] }}
                      transition={{ duration: 0.4 + i * 0.1, repeat: Infinity, repeatType: 'reverse' }}
                      style={{ width: '2.5px', background: '#10b981', borderRadius: '1px' }}
                    />
                  ))}
                  <Typography sx={{ fontFamily: 'monospace', fontSize: '0.62rem', fontWeight: 800, color: '#10b981', ml: 0.5 }}>
                    120 FPS
                  </Typography>
                </Box>
                <Typography sx={{ color: isDarkMode ? '#ffffff' : '#0f172a', fontSize: '0.74rem', fontWeight: 800 }}>
                  CLOUD NLE TIMELINE
                </Typography>
              </Box>
            </Box>

            <Box
              component="img"
              src={heroImage}
              alt="Filmhook App"
              sx={{
                width: '100%',
                maxHeight: { xs: 450, md: 700 },
                display: 'block',
                objectFit: 'contain',
                objectPosition: 'bottom center',
                filter: isDarkMode
                  ? 'drop-shadow(0 30px 60px rgba(7, 164, 245,0.4)) drop-shadow(0 0 30px rgba(93,173,226,0.2))'
                  : 'drop-shadow(0 20px 40px rgba(7, 164, 245,0.25))',
              }}
            />
          </Box>
        </motion.div>
      </Box>

      <Dialog 
        open={videoOpen} 
        onClose={() => setVideoOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: 'transparent',
            boxShadow: 'none',
            overflow: 'visible'
          }
        }}
      >
        <Box sx={{ position: 'relative', width: '100%', pt: '56.25%', background: '#000', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.5)' }}>
          <IconButton 
            onClick={() => setVideoOpen(false)}
            sx={{ position: 'absolute', top: { xs: 8, md: 16 }, right: { xs: 8, md: 16 }, color: 'white', background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)', zIndex: 10, '&:hover': { background: 'rgba(255,255,255,0.3)' } }}
          >
            <CloseIcon />
          </IconButton>
          <iframe 
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
            src="https://www.youtube.com/embed/usY-JkNUDfI?si=6pixuCnqnNGN98g3&autoplay=1" 
            title="Showreel" 
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
            allowFullScreen
          />
        </Box>
      </Dialog>
    </Box>
  );
};

export default Introduction;
